import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { User, FileText, ClipboardList, Pill, Send, Pencil, Trash2, Check, X, MessageSquare, Video, ShieldCheck, ShieldX, Stethoscope } from "lucide-react";
import { format, parseISO } from "date-fns";

interface PatientConsultDrawerProps {
  patientId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Profile {
  first_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  sex: string | null;
  height: string | null;
  weight: number | null;
  phone: string | null;
  state: string | null;
  city: string | null;
}

interface Assessment {
  id: string;
  goal: string | null;
  peptide_interest: string | null;
  conditions: string | null;
  allergies: string | null;
  medications: string | null;
  additional_info: string | null;
  status: string;
  triage_level: string | null;
  decision_reason: string | null;
  decision_at: string | null;
  decided_by: string | null;
  created_at: string;
}

interface Note {
  id: string;
  note: string;
  created_at: string;
}

interface Prescription {
  id: string;
  peptide_name: string;
  dosage: string;
  frequency: string;
  route: string;
  quantity: string;
  refills: number;
  instructions: string | null;
  status: string;
  created_at: string;
}

const PatientConsultDrawer = ({ patientId, open, onOpenChange }: PatientConsultDrawerProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState<string | null>(null);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [messageDraft, setMessageDraft] = useState("");
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [patientEmail, setPatientEmail] = useState<string | null>(null);

  useEffect(() => {
    if (open && patientId) {
      fetchData();
    }
  }, [open, patientId]);

  const fetchData = async () => {
    if (!patientId) return;

    const [profileRes, assessmentRes, notesRes, rxRes] = await Promise.all([
      supabase
        .from("profiles")
        .select("first_name, last_name, date_of_birth, sex, height, weight, phone, state, city")
        .eq("user_id", patientId)
        .single(),
      supabase
        .from("health_assessments")
        .select("id, goal, peptide_interest, conditions, allergies, medications, additional_info, status, triage_level, decision_reason, decision_at, decided_by, created_at")
        .eq("user_id", patientId)
        .order("created_at", { ascending: false }),
      supabase
        .from("patient_notes")
        .select("id, note, created_at")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false }),
      supabase
        .from("prescriptions")
        .select("id, peptide_name, dosage, frequency, route, quantity, refills, instructions, status, created_at")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false }),
    ]);

    if (profileRes.data) setProfile(profileRes.data as any);
    if (assessmentRes.data) setAssessments(assessmentRes.data as any);
    if (notesRes.data) setNotes(notesRes.data as any);
    if (rxRes.data) setPrescriptions(rxRes.data as any);
  };

  const saveNote = async () => {
    if (!newNote.trim() || !patientId || !user) return;
    setSaving(true);
    const { error } = await supabase.from("patient_notes").insert({
      patient_id: patientId,
      author_id: user.id,
      note: newNote.trim(),
    });
    if (error) {
      toast({ title: "Failed to save note", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Note saved" });
      setNewNote("");
      // Refresh notes
      const { data } = await supabase
        .from("patient_notes")
        .select("id, note, created_at")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });
      if (data) setNotes(data as any);
    }
    setSaving(false);
  };

  const refreshNotes = async () => {
    if (!patientId) return;
    const { data } = await supabase
      .from("patient_notes")
      .select("id, note, created_at")
      .eq("patient_id", patientId)
      .order("created_at", { ascending: false });
    if (data) setNotes(data as any);
  };

  const updateNote = async (noteId: string) => {
    if (!editText.trim()) return;
    setSaving(true);
    const { error } = await supabase
      .from("patient_notes")
      .update({ note: editText.trim() })
      .eq("id", noteId);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Note updated" });
      setEditingNote(null);
      await refreshNotes();
    }
    setSaving(false);
  };

  const deleteNote = async (noteId: string) => {
    setDeleting(true);
    const { error } = await supabase
      .from("patient_notes")
      .delete()
      .eq("id", noteId);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Note deleted" });
      setDeleteConfirm(null);
      await refreshNotes();
    }
    setDeleting(false);
  };

  /**
   * Provider clinical decision actions (PRD §4: provider must explicitly choose
   * a decision path; system records who decided and why).
   */
  const recordDecision = async (
    assessmentId: string,
    nextStatus: string,
    reason: string | null,
  ) => {
    if (!user) return false;
    const { error } = await supabase
      .from("health_assessments")
      .update({
        status: nextStatus,
        decision_reason: reason,
        decision_at: new Date().toISOString(),
        decided_by: user.id,
      } as any)
      .eq("id", assessmentId);
    if (error) {
      toast({ title: "Couldn't save decision", description: error.message, variant: "destructive" });
      return false;
    }
    try {
      await supabase.from("audit_logs").insert({
        user_id: user.id,
        action: `assessment_decision_${nextStatus}`,
        resource_type: "health_assessment",
        resource_id: assessmentId,
        metadata: { reason },
      });
    } catch (e) {
      console.warn("audit_logs insert failed", e);
    }
    return true;
  };

  const handleApprove = async () => {
    if (!assessments[0]) return;
    setDecisionLoading("approved");
    const ok = await recordDecision(assessments[0].id, "approved", null);
    if (ok) {
      toast({ title: "Treatment approved", description: "You can now send a prescription from the dashboard." });
      await fetchData();
    }
    setDecisionLoading(null);
  };

  const handleRequireVideo = async () => {
    if (!assessments[0] || !patientId || !user) return;
    setDecisionLoading("video");
    const ok = await recordDecision(assessments[0].id, "video_required", null);
    if (ok) {
      await supabase
        .from("appointments")
        .update({ video_required: true } as any)
        .eq("patient_id", patientId)
        .eq("provider_id", user.id)
        .in("status", ["scheduled", "confirmed"]);
      await supabase.from("messages").insert({
        sender_id: user.id,
        recipient_id: patientId,
        subject: "Live video consultation requested",
        body: "After reviewing your assessment, your provider has requested a live video consultation before proceeding. Please book or reschedule your visit — your provider will share the secure video room link from a HIPAA-compliant vendor.",
      });
      toast({ title: "Video consult requested", description: "Patient has been notified." });
      await fetchData();
    }
    setDecisionLoading(null);
  };

  const handleSendMessage = async () => {
    if (!messageDraft.trim() || !patientId || !user || !assessments[0]) return;
    setDecisionLoading("more_info");
    const ok = await recordDecision(assessments[0].id, "awaiting_info", messageDraft.trim());
    if (!ok) {
      setDecisionLoading(null);
      return;
    }
    const { error } = await supabase.from("messages").insert({
      sender_id: user.id,
      recipient_id: patientId,
      subject: "Quick question about your assessment",
      body: messageDraft.trim(),
    });
    if (error) {
      toast({ title: "Couldn't send message", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Message sent" });
      setMessageDialogOpen(false);
      setMessageDraft("");
      await fetchData();
    }
    setDecisionLoading(null);
  };

  const handleDecline = async () => {
    if (!declineReason.trim() || !assessments[0] || !patientId) return;
    setDecisionLoading("decline");
    const reason = declineReason.trim();
    const ok = await recordDecision(assessments[0].id, "rejected", reason);
    if (ok) {
      try {
        if (patientEmail) {
          await supabase.functions.invoke("send-transactional-email", {
            body: {
              template: "assessment-declined",
              to: patientEmail,
              data: {
                patientName: profile?.first_name || undefined,
                goal: assessments[0].goal || undefined,
                reason,
              },
            },
          });
        }
      } catch (e) {
        console.warn("decline email failed", e);
      }
      toast({ title: "Treatment declined", description: "Patient has been notified." });
      setDeclineDialogOpen(false);
      setDeclineReason("");
      await fetchData();
    }
    setDecisionLoading(null);
  };

  // Resolve patient's email once for the decline-email path.
  useEffect(() => {
    if (!open || !patientId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("get_user_emails");
      if (cancelled) return;
      const match = (data as { user_id: string; email: string }[] | null)?.find((r) => r.user_id === patientId);
      setPatientEmail(match?.email ?? null);
    })();
    return () => { cancelled = true; };
  }, [open, patientId]);

  const patientName = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || "Patient"
    : "Patient";

  const latestAssessment = assessments[0];
  const triageBadgeVariant = latestAssessment?.triage_level === "high"
    ? "destructive"
    : latestAssessment?.triage_level === "moderate"
      ? "secondary"
      : "outline";

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg p-0">
        <ScrollArea className="h-full">
          <div className="p-6 space-y-6">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 font-display">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <User size={18} className="text-primary" />
                </div>
                {patientName}
              </SheetTitle>
            </SheetHeader>

            {/* Profile Summary */}
            {profile && (
              <div className="space-y-2">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <User size={12} /> Patient Profile
                </h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm bg-muted/50 rounded-lg p-3">
                  {profile.date_of_birth && (
                    <>
                      <span className="text-muted-foreground">DOB</span>
                      <span className="text-foreground">{format(parseISO(profile.date_of_birth), "MMM d, yyyy")}</span>
                    </>
                  )}
                  {profile.sex && (
                    <>
                      <span className="text-muted-foreground">Sex</span>
                      <span className="text-foreground capitalize">{profile.sex}</span>
                    </>
                  )}
                  {profile.height && (
                    <>
                      <span className="text-muted-foreground">Height</span>
                      <span className="text-foreground">{profile.height}</span>
                    </>
                  )}
                  {profile.weight && (
                    <>
                      <span className="text-muted-foreground">Weight</span>
                      <span className="text-foreground">{profile.weight} lbs</span>
                    </>
                  )}
                  {(profile.city || profile.state) && (
                    <>
                      <span className="text-muted-foreground">Location</span>
                      <span className="text-foreground">{[profile.city, profile.state].filter(Boolean).join(", ")}</span>
                    </>
                  )}
                  {profile.phone && (
                    <>
                      <span className="text-muted-foreground">Phone</span>
                      <span className="text-foreground">{profile.phone}</span>
                    </>
                  )}
                </div>
              </div>
            )}

            <Separator />

            {/* Latest Assessment */}
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                <ClipboardList size={12} /> Health Assessment
              </h3>
              {latestAssessment ? (
                <div className="space-y-2 bg-muted/50 rounded-lg p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-xs">
                      {format(parseISO(latestAssessment.created_at), "MMM d, yyyy")}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-xs capitalize">{latestAssessment.status.replace(/_/g, " ")}</Badge>
                      {latestAssessment.triage_level && (
                        <Badge variant={triageBadgeVariant as any} className="text-xs capitalize">{latestAssessment.triage_level} risk</Badge>
                      )}
                    </div>
                  </div>
                  {latestAssessment.decision_reason && (
                    <div className="text-xs italic text-muted-foreground border-l-2 border-primary/30 pl-2 mt-1">
                      Provider note: {latestAssessment.decision_reason}
                    </div>
                  )}
                  {latestAssessment.goal && (
                    <div><span className="text-muted-foreground">Goal:</span> <span className="text-foreground">{latestAssessment.goal}</span></div>
                  )}
                  {latestAssessment.peptide_interest && (
                    <div><span className="text-muted-foreground">Peptide Interest:</span> <span className="text-foreground">{latestAssessment.peptide_interest}</span></div>
                  )}
                  {latestAssessment.conditions && (
                    <div><span className="text-muted-foreground">Conditions:</span> <span className="text-foreground">{latestAssessment.conditions}</span></div>
                  )}
                  {latestAssessment.allergies && (
                    <div><span className="text-muted-foreground">Allergies:</span> <span className="text-foreground">{latestAssessment.allergies}</span></div>
                  )}
                  {latestAssessment.medications && (
                    <div><span className="text-muted-foreground">Medications:</span> <span className="text-foreground">{latestAssessment.medications}</span></div>
                  )}
                  {latestAssessment.additional_info && (
                    <div><span className="text-muted-foreground">Notes:</span> <span className="text-foreground">{latestAssessment.additional_info}</span></div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No assessment on file.</p>
              )}
            </div>

            {/* Clinical Decision panel — PRD §4 */}
            {latestAssessment && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                    <Stethoscope size={12} /> Clinical Decision
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <Button size="sm" variant="outline" className="gap-1.5 justify-start" disabled={!!decisionLoading} onClick={() => setMessageDialogOpen(true)}>
                      <MessageSquare size={14} /> Request info
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5 justify-start" disabled={!!decisionLoading} onClick={handleRequireVideo}>
                      <Video size={14} /> Require video
                    </Button>
                    <Button size="sm" className="gap-1.5 justify-start" disabled={!!decisionLoading} onClick={handleApprove}>
                      <ShieldCheck size={14} /> Approve treatment
                    </Button>
                    <Button size="sm" variant="destructive" className="gap-1.5 justify-start" disabled={!!decisionLoading} onClick={() => setDeclineDialogOpen(true)}>
                      <ShieldX size={14} /> Decline
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground italic pt-1">
                    Recorded to the patient's chart and audit log. Prescriptions still require a completed consultation per RLS.
                  </p>
                </div>
              </>
            )}

            <Separator />

            {/* Prescription History */}
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                <Pill size={12} /> Prescription History
              </h3>
              {prescriptions.length > 0 ? (
                <div className="space-y-2">
                  {prescriptions.map((rx) => {
                    const statusVariant = rx.status === "shipped" || rx.status === "filled" ? "default"
                      : rx.status === "cancelled" ? "destructive" : "secondary";
                    return (
                      <div key={rx.id} className="bg-muted/50 rounded-lg p-3 text-sm space-y-1.5">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-foreground">{rx.peptide_name}</p>
                          <Badge variant={statusVariant} className="capitalize text-xs">{rx.status}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {rx.dosage} · {rx.frequency} · {rx.route}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Qty: {rx.quantity} · Refills: {rx.refills} · {format(parseISO(rx.created_at), "MMM d, yyyy")}
                        </p>
                        {rx.instructions && (
                          <p className="text-xs text-muted-foreground/80 italic border-l-2 border-primary/20 pl-2 mt-1">
                            {rx.instructions}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No prescriptions on file.</p>
              )}
            </div>

            <Separator />
            <div className="space-y-3">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                <FileText size={12} /> Consultation Notes
              </h3>
              <div className="space-y-2">
                <Textarea
                  placeholder="Add a note about this consultation..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  className="resize-none text-sm"
                />
                <Button
                  size="sm"
                  onClick={saveNote}
                  disabled={!newNote.trim() || saving}
                  className="gap-1.5"
                >
                  <Send size={12} /> {saving ? "Saving..." : "Save Note"}
                </Button>
              </div>
              {notes.length > 0 && (
                <div className="space-y-2 mt-3">
                  {notes.map((n) => (
                    <div key={n.id} className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
                      {editingNote === n.id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            rows={3}
                            className="resize-none text-sm"
                          />
                          <div className="flex gap-1.5">
                            <Button size="sm" onClick={() => updateNote(n.id)} disabled={!editText.trim() || saving} className="gap-1 h-7 text-xs">
                              <Check size={12} /> {saving ? "Saving..." : "Save"}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingNote(null)} className="gap-1 h-7 text-xs">
                              <X size={12} /> Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-foreground whitespace-pre-wrap flex-1">{n.note}</p>
                            <div className="flex gap-0.5 shrink-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                                onClick={() => { setEditingNote(n.id); setEditText(n.note); }}
                              >
                                <Pencil size={12} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                                onClick={() => setDeleteConfirm(n.id)}
                              >
                                <Trash2 size={12} />
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {format(parseISO(n.created_at), "MMM d, yyyy · h:mm a")}
                          </p>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Note?</DialogTitle>
            <DialogDescription>
              This will permanently delete this consultation note. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirm && deleteNote(deleteConfirm)}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete Note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PatientConsultDrawer;
